// AGC: Historical weather service — fetches daily archive data from backend
// AGC: Uses Open-Meteo Archive API via GET /api/v1/weather/historical

import { getBackendUrl } from '../utils/backendUrl'

export interface HistoricalDailyPoint {
  date: string
  temperatureMin: number
  temperatureMax: number
  temperatureMean: number
  precipitation: number
  radiation: number
  et0: number
  windSpeedMax: number
}

// AGC: v2 cache key prefix — invalidates old cached data from wrong coordinates
const CACHE_KEY_PREFIX = 'droidfarm_hist_wx2_'
const CACHE_DURATION_MS = 6 * 60 * 60 * 1000

const formatDate = (d: Date): string => d.toISOString().slice(0, 10)

const getCacheKey = (lat: number, lng: number, start: string, end: string): string => {
  const rLat = Math.round(lat * 100) / 100
  const rLng = Math.round(lng * 100) / 100
  return `${CACHE_KEY_PREFIX}${rLat}_${rLng}_${start}_${end}`
}

export const fetchHistoricalDaily = async (
  lat: number,
  lng: number,
  startDate: Date,
  endDate: Date,
): Promise<HistoricalDailyPoint[]> => {
  // AGC: Clamp endDate to today-2d (Open-Meteo Archive lag is ~2-3 days)
  const lagBuffer = new Date()
  lagBuffer.setDate(lagBuffer.getDate() - 2)
  const clampedEnd = endDate > lagBuffer ? lagBuffer : endDate

  if (clampedEnd <= startDate) return []

  const startStr = formatDate(startDate)
  const endStr = formatDate(clampedEnd)

  try {
    const cacheKey = getCacheKey(lat, lng, startStr, endStr)
    const cached = localStorage.getItem(cacheKey)
    if (cached) {
      const parsed = JSON.parse(cached)
      if (Date.now() - parsed.ts < CACHE_DURATION_MS) {
        return parsed.data as HistoricalDailyPoint[]
      }
      localStorage.removeItem(cacheKey)
    }
  } catch { /* AGC: cache miss — proceed to fetch */ }

  try {
    const backendUrl = getBackendUrl()
    const url = `${backendUrl}/api/v1/weather/historical?lat=${lat}&lon=${lng}&start_date=${startStr}&end_date=${endStr}`
    const res = await fetch(url)

    if (!res.ok) return []

    const json = await res.json()
    const daily: HistoricalDailyPoint[] = json?.data?.daily ?? []

    if (daily.length > 0) {
      try {
        const cacheKey = getCacheKey(lat, lng, startStr, endStr)
        localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: daily }))
      } catch { /* AGC: storage full — silently skip */ }
    }

    return daily
  } catch {
    return []
  }
}

export const polygonCentroid = (
  coords: [number, number][],
): { lat: number; lng: number } | null => {
  if (!coords || coords.length === 0) return null
  let sumLat = 0
  let sumLng = 0
  for (const [lat, lng] of coords) {
    sumLat += lat
    sumLng += lng
  }
  return { lat: sumLat / coords.length, lng: sumLng / coords.length }
}
